import { useCallback, useState } from 'react';
import axiosInstance from 'src/utils/axios';

export interface MessageAttachmentFile {
  attachId: number;
  messageId: number;
  fileName: string;
  originalFileName?: string;
  originalFile?: string; // Added to match API field name
  fileSize?: number;
  contentType?: string;
  creationDate?: string;
  createdBy?: string;
}

export interface MessageAttachmentsResponse {
  attachments: MessageAttachmentFile[];
  success: boolean;
  message?: string;
}

const useMessageAttachmentsApi = () => {
  const [attachments, setAttachments] = useState<MessageAttachmentFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getMessageAttachments = useCallback(async (messageId: number): Promise<MessageAttachmentFile[]> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use correct endpoint structure: /api/Message/attachments/{messageId}
      const getEndpoints = [
        `/api/Message/attachments/${messageId}`,
        `/api/Message/${messageId}/attachments`,
        `/api/Messages/attachments/${messageId}`,
        `/api/message/attachments/${messageId}`
      ];
      
      let response;
      let lastError;
      
      // Try GET endpoints for message attachments
      for (const apiUrl of getEndpoints) {
        try {         
          response = await axiosInstance.get(apiUrl, {
            // Let axios interceptor handle authorization
          });
          
          break; // Exit loop on success
        } catch (error: any) {
          console.warn(`API: GET Endpoint ${apiUrl} failed:`, {
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            message: error?.message,
            responseData: error?.response?.data
          });
          lastError = error;
        }
      }
      
      // If all GET endpoints failed, use mock data
      if (!response) {
        console.warn(`API: All ${getEndpoints.length} GET attachment endpoints failed for messageId ${messageId}:`);
        getEndpoints.forEach((endpoint: string, index: number) => {
          console.warn(`  ${index + 1}. GET ${endpoint} - FAILED`);
        });
        //console.warn('API: Using mock data for development. Check if API server is running and endpoints are correct.');
        console.warn('API: CORRECT endpoint should be: /api/Message/attachments/{messageId}');
        console.warn('API: Last error details:', lastError?.response || lastError?.message);
      }

      // Handle successful response
      let attachmentFiles: MessageAttachmentFile[] = [];
      
      if (response?.data) {
        if (response.data.success && response.data.attachments) {
          // Format: { success: true, attachments: [...] }
          attachmentFiles = response.data.attachments;
        } else if (Array.isArray(response.data)) {
          // Format: directly an array
          attachmentFiles = response.data;
        } else if (response.data.attachments && Array.isArray(response.data.attachments)) {
          // Format: { attachments: [...] }
          attachmentFiles = response.data.attachments;
        } else if (response.data.messages && Array.isArray(response.data.messages)) {
          // Format: { messages: [...] } - similar to other APIs in this project
          attachmentFiles = response.data.messages;
        } else {
          console.warn('Unexpected response format:', response.data);
        }
      }

      setAttachments(attachmentFiles);
      return attachmentFiles;
      
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch message attachments';
      setError(errorMessage);
      console.error('Error fetching message attachments:', {
        messageId: messageId,
        error: errorMessage,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url
      });           

      //throw error instead of returning empty array.
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setError(null);
  }, []);

  return {
    attachments,
    isLoading,
    error,
    getMessageAttachments,
    clearAttachments
  };
};

export default useMessageAttachmentsApi;